/* Helpers for the takedown vertical (target_type "takedowns" /
   "takedown_shopping"). Keyed on the FDA action registry (action_family /
   action_type / action_label).

   This module is self-contained: the generic `Chip` / `humanize` /
   `formatRecallDate` helpers (which in the sibling current-build live in
   recallUtils) are vendored here so the takedown vertical needs no recall/
   directory. */
import { Gavel, ShieldAlert } from "lucide-react";

/* ─── Generic helpers (vendored, theme-token based) ─── */

export const humanize = (s) =>
  typeof s === "string" && s
    ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "";

/* date_published / date is an ISO-ish "YYYY-MM-DD" string. Render an absolute
   label plus a coarse relative hint. */
export const formatRecallDate = (value) => {
  if (!value) return { absolute: "", relative: "" };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { absolute: String(value), relative: "" };
  const absolute = d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const days = Math.round((Date.now() - d.getTime()) / 86400000);
  let relative = "";
  if (days <= 0) relative = "today";
  else if (days === 1) relative = "yesterday";
  else if (days < 30) relative = `${days}d ago`;
  else if (days < 365) relative = `${Math.round(days / 30)}mo ago`;
  else relative = `${Math.round(days / 365)}y ago`;
  return { absolute, relative };
};

/* Generic muted pill / chip, mirroring the card + detail panel styling. */
export const Chip = ({ children, color, className = "" }) => (
  <span
    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${
      color ? "" : "bg-innerBg text-mutedText border border-surfaceBorder"
    } ${className}`}
    style={
      color
        ? { background: `${color}1a`, color, border: `1px solid ${color}4d` }
        : undefined
    }
  >
    {children}
  </span>
);

/* ─── Action family → label + accent color ───
   The crawler resolves every FDA Action/Additional-Outcome label into a
   canonical family (crawler/actions.py). The family sets the badge color; the
   more specific action_label ("Warning Letter", "Public Notification") is
   preferred as the badge text when present. */
const FAMILY_META = {
  product_notice: { label: "Consumer Notice", color: "#f43f5e" }, // rose — safety warning
  recall: { label: "Recall", color: "#f59e0b" }, // amber
  enforcement: { label: "Enforcement", color: "#f97316" }, // orange — warning letter
  legal_action: { label: "Legal Action", color: "#6366f1" }, // indigo — court
  close_out: { label: "Closed Out", color: "#10b981" }, // emerald — resolved
  info: { label: "Informational", color: "#64748b" }, // slate
  unknown: { label: "Action", color: "#94a3b8" }, // slate fallback
};

export const actionMeta = (article) => {
  const base = FAMILY_META[article?.action_family] || FAMILY_META.unknown;
  const label = article?.action_label || article?.action || base.label;
  return { label, color: base.color };
};

/* ─── Recommended vendor action ───
   Each confirmed vendor still selling the product is an enforcement target with an
   action the FDA team should take, computed by the crawler (crawler/pipeline.py):
   `warning_letter` for any store selling a flagged product, or `second_warning_letter`
   for a store that was already warned for this product and is STILL selling (not
   complying). `icon` is a string key resolved to a lucide component in the chip. */
const RECOMMENDED_ACTION = {
  warning_letter: {
    label: "Needs warning letter",
    short: "to warn",
    color: "#f97316",
    icon: "gavel",
  },
  second_warning_letter: {
    label: "Needs 2nd warning letter",
    short: "2nd letter",
    color: "#ef4444",
    icon: "alert",
  },
};

export const recommendedActionMeta = (action) =>
  RECOMMENDED_ACTION[action] || null;

const ACTION_ICON = {
  gavel: Gavel,
  alert: ShieldAlert,
};

/* Chip naming the enforcement action a vendor needs. `count` (optional) renders
   the terse "{n} to warn" card variant; omitting it renders the full label.
   Renders nothing when the action is missing/unknown (e.g. an excluded vendor). */
export const RecommendedActionChip = ({ action, count, className = "" }) => {
  const meta = recommendedActionMeta(action);
  if (!meta) return null;
  const Icon = ACTION_ICON[meta.icon] || Gavel;
  return (
    <Chip color={meta.color} className={className}>
      <Icon size={10} className="shrink-0" />
      {count != null ? `${count} ${meta.short}` : meta.label}
    </Chip>
  );
};

/* ─── Availability discovery-channel label ───
   Each listing records how it was found. reverse_image (matched the FDA product
   photo) is the most precise, so it gets a distinct emerald highlight; the rest
   are humanized engine names ("bing_shopping", "duckduckgo", …). */
export const sourceMeta = (source) => {
  if (!source) return null;
  if (source === "reverse_image") return { label: "Photo match", color: "#10b981" };
  if (source === "bing_shopping") return { label: "Shopping", color: undefined };
  return {
    label:
      typeof source === "string"
        ? source.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
        : "",
    color: undefined,
  };
};
